import type {
	Co2EmissionUnit,
	CurrencyDTO,
	CurrencyType,
	DistanceUnit,
	DurationUnit,
	WeightUnit,
} from '@uturn/api/v1';
import type { ShippingLocationDto } from '@uturn/api/v2';
import { type AxiosResponse, isAxiosError } from 'axios';

export function copyValue(value: string) {
	navigator.clipboard.writeText(value);
}

// TODO: implement user setting instead of window.navigator.language
const browserPreferredLanguage = window.navigator.language; // 'nl-NL',

export function formatPrice(amount: number, currency: CurrencyType | string) {
	return new Intl.NumberFormat(browserPreferredLanguage, {
		style: 'currency',
		currency,
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	}).format(amount);
}

/** @deprecated Use formatPrice instead. */
export function priceToCurrency(
	amount: number,
	currency: CurrencyType | string,
) {
	return formatPrice(amount, currency);
}

export function currencySymbol(currency: string) {
	return new Intl.NumberFormat(browserPreferredLanguage, {
		style: 'currency',
		currency,
	})
		.formatToParts(1)
		.find((x) => x.type === 'currency')?.value;
}

export function formatDistance(amount: number, unit: DistanceUnit) {
	return new Intl.NumberFormat(browserPreferredLanguage, {
		style: 'unit',
		unit,
		unitDisplay: 'short',
		maximumFractionDigits: 0,
		minimumFractionDigits: 0,
	}).format(amount);
}

export function formatCo2Emission(
	amount: number,
	unit: Co2EmissionUnit | string,
) {
	return new Intl.NumberFormat(browserPreferredLanguage, {
		style: 'unit',
		unit,
		unitDisplay: 'short',
		maximumFractionDigits: 0,
		minimumFractionDigits: 0,
	}).format(amount);
}

export function formatWeight(amount: number, unit: WeightUnit | string) {
	return new Intl.NumberFormat(browserPreferredLanguage, {
		style: 'unit',
		unit,
		unitDisplay: 'short',
		maximumFractionDigits: 0,
		minimumFractionDigits: 0,
	}).format(amount);
}

export function formatDuration(amount: number, unit: DurationUnit | string) {
	return new Intl.NumberFormat(browserPreferredLanguage, {
		style: 'unit',
		unit,
		unitDisplay: 'short',
		maximumFractionDigits: 1,
		minimumFractionDigits: 0,
	}).format(amount);
}

const defaultCurrencySign = '€';

/**
 * Returns currency sign for a given currency code.
 * @param currencyCode
 * @param currencies
 * @returns currencySign
 * @example getCurrencySign('EUR', shipmentMetaData?.currencies) => "€"
 */
export function getCurrencySign(
	currencyCode?: string,
	currencies?: CurrencyDTO[],
): string {
	if (!currencyCode || !currencies) {
		return defaultCurrencySign;
	}
	return (
		currencies.find((currency) => currency.code === currencyCode)?.sign ??
		defaultCurrencySign
	);
}

export function downloadFile(request: Promise<unknown>, fileName: string) {
	return (request as Promise<AxiosResponse>).then((response) => {
		const url = window.URL.createObjectURL(new Blob([response.data]));
		const link = document.createElement('a');

		link.href = url;
		link.setAttribute('download', fileName);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	});
}

export function queryDownloadFile(response: any, fileName: string) {
	const fileBlob = response.data;
	const blob = new Blob([fileBlob], {
		type: 'application/octet-stream',
	});

	const extension = fileBlob.type.substring(fileBlob.type.lastIndexOf('/') + 1);

	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');

	link.href = url;
	link.setAttribute('download', `${fileName}.${extension}`);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

export function sortData(
	data: [Record<string, number | string>],
	key: string,
	direction: 'asc' | 'desc',
) {
	return [...data].sort((a, b) => {
		if (a[key] === null || a[key] === undefined) {
			return 1;
		}
		if (b[key] === null || b[key] === undefined) {
			return -1;
		}
		if (
			(a[key] === null && b[key] === null) ||
			(a[key] === undefined && b[key] === undefined)
		) {
			return 0;
		}
		return (
			a[key].toString().localeCompare(b[key]?.toString(), 'en', {
				numeric: true,
			}) * (direction === 'asc' ? 1 : -1)
		);
	});
}

export function getLocationQueryString(location: any) {
	if (location === undefined) {
		return '';
	}

	if (typeof location === 'string') {
		return location;
	}

	// TODO: Clean this garbage up
	return [
		location.name,
		location.street
			? location.street +
				(location.houseNumber ? ` ${location.houseNumber}` : '')
			: undefined,
		location.post ? `${location.postalCode} ${location.city}` : location.city,
		location.countryCode,
	]
		.filter((part) => part !== undefined)
		.join(', ');
}

/**
 * Return location city.
 * When city isn't available, show a combination of postal and country code.
 *
 * @param location ShippingLocationDto
 * @returns
 *
 * @see ShipmentCard
 */
export function getLocationCity(location: ShippingLocationDto): string {
	if (location === undefined) {
		return '';
	}

	if (location.address?.city) {
		return location.address.city;
	}

	if (location.name && location.address?.countryCode) {
		if (location.address?.postalCode) {
			return `${location.name}, ${location.address.postalCode}, ${location.address.countryCode}`;
		}
		return `${location.name}, ${location.address.countryCode}`;
	}

	return location.name;
}

export function toTitle(data: string) {
	return data.charAt(0).toUpperCase() + data.slice(1).toLowerCase();
}

function isErrorWithMessage(error: unknown): error is { message: string } {
	return (
		!!error &&
		typeof error === 'object' &&
		'message' in error &&
		typeof error.message === 'string'
	);
}

/** Temporary solution until Java side returns that error code separately. */
function parseErrorCode(message: string | undefined) {
	if (!message) {
		return undefined;
	}
	if (message.includes('CREDIT_LIMIT_EXCEEDED')) {
		return 'CREDIT_LIMIT_EXCEEDED';
	}
	return undefined;
}

export function getApiErrorMessage(error: unknown) {
	let message: string | undefined;
	let errCode: string | undefined;

	if (isAxiosError(error)) {
		message = error.response?.data?.message || error.response?.data?.error;
		errCode = parseErrorCode(error.response?.data?.message);
	} else if (error instanceof Error) {
		message = error.message;
		errCode = parseErrorCode(message);
	} else if (isErrorWithMessage(error)) {
		message = error.message;
		errCode = parseErrorCode(message);
	}
	if (errCode) {
		return errCode;
	}

	return message;
}

export function getApiErrorStatus(error: unknown) {
	if (isAxiosError(error)) {
		const status = error.response?.data?.status || error.status;
		return status ? Number(status) : undefined;
	}

	return undefined;
}

export function isSelected<T>(value: T, items: T[]): boolean {
	return items.findIndex((item) => item === value) !== -1;
}

/** @deprecated Use formatDate or formatDatetime instead. */
export function dateFormatter(options: Intl.DateTimeFormatOptions = {}) {
	return new Intl.DateTimeFormat(browserPreferredLanguage, options);
}

/** @deprecated */
export function stringRound(num: string, decimals: number) {
	if (!Number(num)) {
		return num;
	}

	return Math.round(Number(num) * decimals) / decimals;
}

/** @deprecated */
/** Checks if the pathname is a shipment number
 * @param pathname pathname to check if it is a shipment number
 */
export function pathIsShipmentNumber(pathname: string): boolean {
	const path = pathname.replace('/', '');

	return path.length === 8 && path[0] === '7' && !Number.isNaN(Number(path));
}
